import * as React from 'react'

import { SheetSectionCard } from '@/components/SheetSectionCard'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { CLIENT_TIMELINE_PAGE_SIZE } from '@/config/constants'
import type { ClientActivity } from '@/features/patients/hooks/useClientActivity'
import type { ClientHistory } from '@/features/patients/hooks/useClientHistory'
import {
  buildTimeline,
  groupTimeline,
  type TimelineKind,
} from '@/features/patients/utils/clientTimeline'
import { ClientTimelineEntry } from '../ClientTimelineEntry'
import {
  KIND_LABELS,
  TimelineFilters,
  timelineKinds,
  type TimelineFilter,
} from '../ClientTimelineFilters'

const NO_ROWS: never[] = []

/**
 * Everything that has happened with the client, newest first: calls, duty
 * assignments, visits and appointments, with upcoming appointments on top.
 * Filter chips narrow it to one kind; older entries load on request.
 */
export function ClientActivityTab({
  activity,
  history,
  now,
}: {
  activity: ClientActivity
  history: ClientHistory
  now: Date
}) {
  const kinds = timelineKinds(activity, history)
  const [filter, setFilter] = React.useState<TimelineFilter>('all')
  const [limit, setLimit] = React.useState(CLIENT_TIMELINE_PAGE_SIZE)
  const { calls } = activity

  // A string key keeps the selection stable while the visible kinds are.
  const kindKey = kinds.map((state) => state.kind).join(',')
  const selected = React.useMemo(
    () =>
      new Set<TimelineKind>(
        filter === 'all' ? (kindKey.split(',') as TimelineKind[]) : [filter]
      ),
    [filter, kindKey]
  )

  // Keyed on the row arrays themselves, which keep their identity between
  // renders, so the feed is rebuilt only when a source's rows change.
  const visitRows = history.visits.allowed ? history.visits.rows : NO_ROWS
  const appointmentRows = history.appointments.allowed
    ? history.appointments.rows
    : NO_ROWS
  const items = React.useMemo(
    () =>
      buildTimeline(
        {
          calls: calls.rows,
          callsTruncated: calls.hasMore,
          duties: activity.duties.rows,
          visits: visitRows,
          appointments: appointmentRows,
        },
        selected,
        now
      ),
    [
      calls.rows,
      calls.hasMore,
      activity.duties.rows,
      visitRows,
      appointmentRows,
      selected,
      now,
    ]
  )
  const groups = groupTimeline(items.slice(0, limit), now)

  const isLoading = kinds.some(
    (state) => selected.has(state.kind) && state.isLoading
  )
  const hasOlder =
    items.length > limit || (selected.has('call') && calls.hasMore)
  // A kind that failed to load leaves a hole in the feed; say so rather than
  // let the feed pass for complete.
  const failed = kinds.filter(
    (state) => selected.has(state.kind) && state.isError
  )

  function choose(next: TimelineFilter) {
    setFilter(next)
    setLimit(CLIENT_TIMELINE_PAGE_SIZE)
  }

  function loadOlder() {
    // Out of loaded entries: fetch the next page of calls, which is what
    // holds the rest of the feed back.
    if (items.length <= limit) calls.loadMore()
    setLimit((value) => value + CLIENT_TIMELINE_PAGE_SIZE)
  }

  return (
    <SheetSectionCard
      title="Timeline"
      body={
        <div className="flex flex-col">
          <div className="mb-3.5">
            <TimelineFilters kinds={kinds} value={filter} onChange={choose} />
          </div>

          {failed.length > 0 ? (
            <p className="text-muted-foreground mb-3 text-sm" role="status">
              Could not load{' '}
              {failed
                .map((state) => KIND_LABELS[state.kind].toLowerCase())
                .join(' or ')}
              ; the timeline below is missing them.
            </p>
          ) : null}

          {/* Merging a partial feed would reorder under the reader as each
              kind lands, so wait for all of them. */}
          {isLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : groups.length === 0 && failed.length === 0 ? (
            <p className="text-muted-foreground py-6 text-center text-sm">
              {filter === 'all'
                ? 'Nothing has happened with this client yet'
                : `No ${KIND_LABELS[filter].toLowerCase()} for this client`}
            </p>
          ) : groups.length === 0 ? null : (
            <div className="flex flex-col gap-5">
              {groups.map((group) => (
                <section key={group.label} aria-label={group.label}>
                  <div className="mb-3 flex items-center gap-2.5">
                    <span className="text-muted-foreground text-xs font-medium tracking-wider whitespace-nowrap uppercase">
                      {group.label}
                    </span>
                    <span
                      aria-hidden
                      className="bg-neutral-hover-bg h-px flex-1"
                    />
                  </div>
                  <ul className="before:bg-neutral-active-bg relative flex flex-col gap-3.5 pl-[34px] before:absolute before:top-3 before:bottom-3 before:left-[11.5px] before:w-[1.5px] before:rounded-sm">
                    {group.items.map((item) => (
                      <ClientTimelineEntry
                        key={`${item.kind}:${item.id}`}
                        item={item}
                        now={now}
                        providerNames={history.providerNames}
                      />
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          )}

          {hasOlder ? (
            <div className="mt-3.5 flex justify-center">
              <Button
                variant="outline"
                size="sm"
                disabled={calls.isLoadingMore}
                onClick={loadOlder}>
                {calls.isLoadingMore ? 'Loading…' : 'Load older activity'}
              </Button>
            </div>
          ) : null}
        </div>
      }
    />
  )
}
